// ============================================================================
// Web Push helpers — iscrizione/cancellazione notifiche per il dipendente
// ============================================================================
// La chiave pubblica VAPID è — per design — pubblica: viene esposta al client
// per permettere a PushManager.subscribe() di legare la subscription al server
// che possiede la chiave privata. Hardcodarla è la prassi standard.
// ============================================================================

import { supabase, isSupabaseConfigured } from "./supabase";

export const VAPID_PUBLIC_KEY =
  "BIFE2Xqjlcnd1IY6qBNfJKhy4fHLlWM6s5DQsasnLn_pD_Z6rmqKPhzCmSCr0LgyoI4K87mjIO_8S6TIiHz6ENQ";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// iOS: il push funziona solo se la PWA è stata installata sulla home
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua);
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  return navigator.serviceWorker.ready;
}

export async function getPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeForDipendente(dipId) {
  if (!isPushSupported()) throw new Error("Push non supportato su questo dispositivo");
  if (!isSupabaseConfigured) throw new Error("Supabase non configurato");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Permesso notifiche negato. Abilitalo dalle impostazioni."
        : "Permesso notifiche non concesso"
    );
  }

  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker non registrato");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = sub.toJSON ? sub.toJSON() : null;
  const endpoint = sub.endpoint;
  const p256dh = json?.keys?.p256dh ?? arrayBufferToBase64(sub.getKey("p256dh"));
  const auth   = json?.keys?.auth   ?? arrayBufferToBase64(sub.getKey("auth"));

  const row = {
    dip_id: Number(dipId),
    endpoint,
    p256dh,
    auth,
    user_agent: navigator.userAgent || null,
    updated_at: new Date().toISOString(),
  };

  // upsert su endpoint (UNIQUE) — se la sub esiste già aggiorna dip_id/keys
  const { error } = await supabase
    .from("push_subscriptions")
    .upsert(row, { onConflict: "endpoint" });
  if (error) throw error;

  return sub;
}

export async function unsubscribeForDipendente() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    if (isSupabaseConfigured) {
      await supabase.from("push_subscriptions").delete().eq("endpoint", sub.endpoint);
    }
  } catch {}
  try { await sub.unsubscribe(); } catch {}
}
